import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Button, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaArrowLeft, FaCheckCircle, FaCalendarAlt, FaUserTag, FaFlag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import mockRnDService from '../../services/mockRnDService';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const data = await mockRnDService.getProject(parseInt(id));
        setProject(data);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Gagal memuat detail proyek. ' + err.message);
        toast.error('Gagal memuat detail proyek');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  const getBadgeVariant = (priority) => {
    if (!priority) return 'secondary';
    switch (priority.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getProgressStepStatus = (phase, step) => {
    if (!project || !project.progress) return 'pending';
    
    switch (step) {
      case 'research':
        return project.progress.research ? 'completed' : 'pending';
      case 'production':
        return project.progress.production ? 'completed' : 
               project.progress.research ? 'active' : 'pending';
      case 'photo':
        return project.progress.photo ? 'completed' : 
               project.progress.production ? 'active' : 'pending';
      case 'video':
        return project.progress.video ? 'completed' : 
               project.progress.photo ? 'active' : 'pending';
      case 'finished':
        return project.progress.finished ? 'completed' : 
               project.progress.video ? 'active' : 'pending';
      default:
        return 'pending';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-3">Memuat detail proyek...</p>
          </div>
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            {error}
          </Alert>
          <Button 
            variant="primary" 
            as={Link} 
            to="/rnd/projects"
          >
            <FaArrowLeft className="me-2" />
            Kembali ke Daftar Proyek
          </Button>
        </Container>
      </MainLayout>
    );
  }

  if (!project) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <Alert variant="warning">
            Proyek tidak ditemukan
          </Alert>
          <Button 
            variant="primary" 
            as={Link} 
            to="/rnd/projects"
          >
            <FaArrowLeft className="me-2" />
            Kembali ke Daftar Proyek
          </Button>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Detail Proyek</h4>
              <div>
                <Button 
                  variant="primary" 
                  className="me-2"
                  as={Link} 
                  to="/rnd/projects"
                >
                  <FaArrowLeft className="me-1" /> Kembali
                </Button>
                <Button 
                  variant="success" 
                  className="me-2"
                  as={Link}
                  to={`/rnd/projects/${id}/edit`}
                >
                  <FaEdit className="me-1" /> Edit
                </Button>
                <Button 
                  variant="info"
                  as={Link}
                  to={`/rnd/projects/${id}/progress`}
                >
                  <FaCheckCircle className="me-1" /> Progress
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <h2>{project.name}</h2>
                
                <div className="mb-4">
                  <Badge 
                    bg={project.status === 'finished' ? 'success' : 'primary'} 
                    className="me-2"
                  >
                    {project.status === 'finished' ? 'Selesai' : 'Aktif'}
                  </Badge>
                  
                  <Badge 
                    bg={getBadgeVariant(project.priority)} 
                    className="me-2"
                  >
                    Prioritas: {project.priority === 'high' ? 'Tinggi' : 
                                project.priority === 'medium' ? 'Menengah' : 'Rendah'}
                  </Badge>
                  
                  <Badge bg="info">
                    Fase: {project.phase === 'research' ? 'Penelitian' : 
                           project.phase === 'production' ? 'Produksi' : 
                           project.phase === 'photo' ? 'Foto' : 
                           project.phase === 'video' ? 'Video' : 
                           project.phase === 'finished' ? 'Selesai' : 'Unknown'}
                  </Badge>
                </div>
                
                <Card className="mb-4">
                  <Card.Header>Deskripsi</Card.Header>
                  <Card.Body>
                    <p>{project.description || "Tidak ada deskripsi"}</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={4}>
                <Card className="mb-4">
                  <Card.Header>Informasi Proyek</Card.Header>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <FaCalendarAlt className="me-2 text-primary" />
                      <strong>Tanggal Mulai:</strong> {project.start_date}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaCalendarAlt className="me-2 text-danger" />
                      <strong>Target Selesai:</strong> {project.target_date}
                    </ListGroup.Item>
                    {project.end_date && (
                      <ListGroup.Item>
                        <FaCalendarAlt className="me-2 text-success" />
                        <strong>Tanggal Selesai:</strong> {project.end_date}
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item>
                      <FaUserTag className="me-2 text-info" />
                      <strong>Manager:</strong> {project.manager || "Belum ditentukan"}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <FaFlag className="me-2 text-warning" />
                      <strong>Status:</strong> {project.status === 'finished' ? 'Selesai' : 'Aktif'}
                    </ListGroup.Item>
                  </ListGroup>
                </Card>
                
                <Card>
                  <Card.Header>Status Progress</Card.Header>
                  <ListGroup variant="flush">
                    <ListGroup.Item className={`d-flex justify-content-between align-items-center ${project.progress?.research ? 'bg-light' : ''}`}>
                      <span>Penelitian</span>
                      {project.progress?.research ? 
                        <Badge bg="success" pill>Selesai</Badge> : 
                        <Badge bg="secondary" pill>Pending</Badge>
                      }
                    </ListGroup.Item>
                    <ListGroup.Item className={`d-flex justify-content-between align-items-center ${project.progress?.production ? 'bg-light' : ''}`}>
                      <span>Produksi</span>
                      {project.progress?.production ? 
                        <Badge bg="success" pill>Selesai</Badge> : 
                        project.progress?.research ? 
                          <Badge bg="primary" pill>Aktif</Badge> : 
                          <Badge bg="secondary" pill>Pending</Badge>
                      }
                    </ListGroup.Item>
                    <ListGroup.Item className={`d-flex justify-content-between align-items-center ${project.progress?.photo ? 'bg-light' : ''}`}>
                      <span>Foto</span>
                      {project.progress?.photo ? 
                        <Badge bg="success" pill>Selesai</Badge> : 
                        project.progress?.production ? 
                          <Badge bg="primary" pill>Aktif</Badge> : 
                          <Badge bg="secondary" pill>Pending</Badge>
                      }
                    </ListGroup.Item>
                    <ListGroup.Item className={`d-flex justify-content-between align-items-center ${project.progress?.video ? 'bg-light' : ''}`}>
                      <span>Video</span>
                      {project.progress?.video ? 
                        <Badge bg="success" pill>Selesai</Badge> : 
                        project.progress?.photo ? 
                          <Badge bg="primary" pill>Aktif</Badge> : 
                          <Badge bg="secondary" pill>Pending</Badge>
                      }
                    </ListGroup.Item>
                    <ListGroup.Item className={`d-flex justify-content-between align-items-center ${project.progress?.finished ? 'bg-light' : ''}`}>
                      <span>Final</span>
                      {project.progress?.finished ? 
                        <Badge bg="success" pill>Selesai</Badge> : 
                        project.progress?.video ? 
                          <Badge bg="primary" pill>Aktif</Badge> : 
                          <Badge bg="secondary" pill>Pending</Badge>
                      }
                    </ListGroup.Item>
                  </ListGroup>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default ProjectDetail;